import { Fragment, useEffect, useState } from "react";

import { Printer, X, Search, ChevronDown, ChevronRight } from "lucide-react";

import CustomModalForm from "@/components/desktop/form/modal/CustomModalForm/CustomModalForm";
import CustomButton from "@/components/desktop/button/CustomButton";
import Fetching from "@/components/Fetching/Fetching";
import CustomInputSelect from "@/components/desktop/form/input/input-select/CustomInputSelect.jsx";
import {
	useApplicationOrderNecessity,
	useApplicationOrderNecessityPDF,
	useApplicationOrderNecessityLookups,
} from "@/hooks/planning/application-order/useApplicationOrderNecessity";
import { formatMilesDecimalNew, formatUnitMeasure } from "@/services/Utils";

import styles from "./modal-oa-necessity.module.scss";

const OANecessity = ({ applicationCodes, onClose }) => {
	const [formState, setFormState] = useState({
		estate_parent_id: [],
		estate_id: [],
	});

	const { data: lookupsData, isLoading: isLoadingLookups } = useApplicationOrderNecessityLookups();

	const [estateParentOptions, setEstateParentOptions] = useState([]);
	const [filteredEstateOptions, setFilteredEstateOptions] = useState([]);
	const [allEstates, setAllEstates] = useState([]);
	const [appliedFilters, setAppliedFilters] = useState({ estate_id: [] });
	const [expandedProducts, setExpandedProducts] = useState({});

	useEffect(() => {
		if (lookupsData && lookupsData.estates_parents) {
			setEstateParentOptions(lookupsData.estates_parents.map((estateParent) => ({ value: estateParent.value, label: estateParent.label })));

			const flattenedEstates = lookupsData.estates_parents.flatMap((estateParent) =>
				estateParent.estates.map((estate) => ({
					...estate,
					estate_parent_id: estateParent.value,
				})),
			);
			setAllEstates(flattenedEstates);
			setFilteredEstateOptions(flattenedEstates);

			const initialEstateIds = flattenedEstates.map((estate) => estate.value);
			setFormState({
				estate_parent_id: lookupsData.estates_parents.map((estateParent) => estateParent.value),
				estate_id: initialEstateIds,
			});
			setAppliedFilters({ estate_id: initialEstateIds });
		}
	}, [lookupsData]);

	const { data, isLoading } = useApplicationOrderNecessity(applicationCodes, appliedFilters.estate_id);
	const { mutate: downloadPDF, isLoading: isLoadingPDF } = useApplicationOrderNecessityPDF(applicationCodes);

	if (!applicationCodes) {
		return null;
	}

	const handlePrint = () => {
		downloadPDF(
			{
				codes: applicationCodes,
				estates: formState.estate_id,
			},
			{
				onSuccess: () => {},
			},
		);
	};

	const handleEstateParentsChange = (value) => {
		setFormState((previousState) => {
			const validEstateIds = allEstates
				.filter((estate) => !value || value.length === 0 || value.includes(estate.estate_parent_id))
				.map((estate) => estate.value);
			const newEstateIds = previousState.estate_id.filter((estateId) => validEstateIds.includes(estateId));

			return {
				...previousState,
				estate_parent_id: value || [],
				estate_id: newEstateIds,
			};
		});

		const filtered = allEstates.filter((estate) => !value || value.length === 0 || value.includes(estate.estate_parent_id));
		setFilteredEstateOptions(filtered);
	};

	const handleEstateChange = (value) => {
		setFormState((previousState) => ({ ...previousState, estate_id: value || [] }));
	};

	const handleFilter = () => {
		setAppliedFilters({ estate_id: formState.estate_id });
	};

	const toggleProductDetails = (productKey) => {
		setExpandedProducts((previousExpandedProducts) => ({
			...previousExpandedProducts,
			[productKey]: !previousExpandedProducts[productKey],
		}));
	};

	return (
		<CustomModalForm
			open={Boolean(applicationCodes)}
			onClose={onClose}
			title="Calcular necesidad de compra"
			size="xl"
			closeOnBackdrop={false}
			closeOnEsc={false}
			className="printableModal"
			footer={
				<>
					<CustomButton variant="primary" startIcon={<Printer size={16} />} onClick={handlePrint} loading={isLoadingPDF}>
						Imprimir
					</CustomButton>
					<CustomButton variant="secondary" startIcon={<X size={16} />} onClick={onClose}>
						Cerrar
					</CustomButton>
				</>
			}
		>
			{isLoading || isLoadingPDF || isLoadingLookups ? (
				<Fetching newStyles={{ marginTop: "2em", marginBottom: "2em" }} />
			) : (
				<>
					<p className={styles.description}>
						Se está calculando la necesidad de compra para las órdenes de aplicación con los siguientes códigos:{" "}
						<span className={styles.orderCodes}>{data?.codes}</span>.
					</p>
					<p className={styles.description}>
						La <i> Cantidad en Inventario </i> no incluye los productos que se encuentran fuera de bodega
					</p>
					<div className={styles.selectors}>
						<CustomInputSelect
							className={styles.customSelect}
							label="Campos"
							name="estate_parent_id"
							isMulti
							options={estateParentOptions}
							value={formState.estate_parent_id || []}
							onChange={handleEstateParentsChange}
							required
							col={6}
						/>
						<CustomInputSelect
							className={styles.customSelect}
							label="Bodegas"
							name="estate_id"
							isMulti
							options={filteredEstateOptions}
							value={formState.estate_id || []}
							onChange={handleEstateChange}
							required
							col={6}
						/>
						<div className={styles.buttonSearch}>
							<CustomButton variant="primary" startIcon={<Search size={16} />} onClick={handleFilter} loading={isLoading}>
								Filtrar
							</CustomButton>
						</div>
					</div>
					<div className={styles.tableContainer}>
						<table className={styles.table}>
							<thead>
								<tr>
									<th className={styles.minimalColumn}>Producto</th>
									<th className={styles.minimalColumn}>
										Ingrediente <br /> Activo
									</th>
									<th className={styles.minimalColumn}>
										Gasto <br /> Total
									</th>
									<th className={styles.minimalColumn}>
										Cantidad <br /> Pendiente
									</th>
									<th className={styles.minimalColumn}>
										Cantidad <br /> en Inventario
									</th>
									<th className={styles.minimalColumn}>
										Cantidad <br />a Comprar
									</th>
								</tr>
							</thead>
							<tbody>
								{data &&
									data?.products?.map((item, index) => {
										const productKey = `${item.product_name}-${index}`;
										const isExpanded = !!expandedProducts[productKey];
										const quantityInventory = Object.values(item.quantity_inventory).reduce((sum, quantity) => sum + quantity, 0);

										let need = item.quantity_pending - quantityInventory;
										if (need < 0) {
											need = 0;
										}

										const quantityTotal = String(item.quantity_total).replace(".", ",");
										const quantityPending = String(item.quantity_pending).replace(".", ",");
										const quantityInventoryText = String(quantityInventory).replace(".", ",");
										const quantityNeed = String(need).replace(".", ",");
										const inventoryByEstate = Object.entries(item.quantity_inventory || {}).filter(([, quantity]) => Number(quantity) > 0);

										return (
											<Fragment key={productKey}>
												<tr className={`${styles.summaryRow}${need === 0 ? ` ${styles.hideWhenPrinting}` : ""}`}>
													<td className={`${styles.minimalColumn} ${styles.productCell}`}>
														<button
															type="button"
															className={styles.productToggleButton}
															onClick={() => toggleProductDetails(productKey)}
														>
															<span className={styles.collapseToggleButton}>
																{isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
															</span>
															<span>{item.product_name}</span>
														</button>
													</td>
													<td className={styles.minimalColumn}>{item.active_ingredient}</td>
													<td className={`${styles.minimalColumn} ${styles.right}`}>
														{formatMilesDecimalNew(quantityTotal)}
														{formatUnitMeasure(item.unit_measurement)}
													</td>
													<td className={`${styles.minimalColumn} ${styles.right}`}>
														{formatMilesDecimalNew(quantityPending)}
														{formatUnitMeasure(item.unit_measurement)}
													</td>
													<td className={`${styles.minimalColumn} ${styles.right}`}>
														{formatMilesDecimalNew(quantityInventoryText)}
														{formatUnitMeasure(item.unit_measurement)}
													</td>
													<td className={`${styles.minimalColumn} ${styles.right}`}>
														{formatMilesDecimalNew(quantityNeed)}
														{formatUnitMeasure(item.unit_measurement)}
													</td>
												</tr>
												{isExpanded && inventoryByEstate.length === 0 && (
													<tr className={`${styles.emptyStateRow}${need === 0 ? ` ${styles.hideWhenPrinting}` : ""}`}>
														<td colSpan={6} className={`${styles.minimalColumn} ${styles.emptyStateCell}`}>
															No hay stock disponible en ninguna bodega para este producto.
														</td>
													</tr>
												)}
												{isExpanded &&
													inventoryByEstate.map(([estateId, quantity]) => {
														const estate = allEstates.find((e) => e.value === parseInt(estateId));
														const quantityInventoryByEstate = String(quantity).replace(".", ",");

														const estateParent = lookupsData?.estates_parents?.find((ep) => ep.value === estate?.estate_parent_id);
														const estateParentLabel = estateParent?.label || "";
														const displayLabel =
															estateParentLabel && estate?.label
																? `${estateParentLabel}, ${estate.label}`
																: estate?.label || `Bodega ${estateId}`;

														return (
															<tr
																key={`${productKey}-${estateId}`}
																className={`${styles.detailRow}${need === 0 ? ` ${styles.hideWhenPrinting}` : ""}`}
															>
																<td colSpan={4} className={`${styles.minimalColumn} ${styles.detailLabelCell}`}>
																	{displayLabel}
																</td>
																<td className={`${styles.minimalColumn} ${styles.right}`}>
																	{formatMilesDecimalNew(quantityInventoryByEstate)}
																	{formatUnitMeasure(item.unit_measurement)}
																</td>
																<td className={styles.minimalColumn}></td>
															</tr>
														);
													})}
											</Fragment>
										);
									})}
							</tbody>
						</table>
					</div>
				</>
			)}
		</CustomModalForm>
	);
};

export default OANecessity;
